import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import TextField from "@mui/material/TextField";
import { Country, State, City } from "country-state-city";
import useEncryption from "../../../services/useEncryption";
import { stepTwelveSchema } from "../../../services/formValidator/providerSignupValidator";
import {
  createAuditLogs,
  getContactInfo,
  submitContactInfo,
} from "../../../api/providerApi/providerApi";
import { toast } from "react-toastify";
import path from "../../../path";
import useHeaders from "../../../helpers/useHeaders";
import { useSelector } from "react-redux";
import { selectLoc } from "../../../redux/slice/locSlice";

export const Steptwelve = ({ providerId }) => {
  const { encryptData, decryptData } = useEncryption();
  // const [enableField, setEnableFields] = useState(true);
  const headers = useHeaders();
  const { loc } = useSelector(selectLoc);
  const [notes, setNotes] = useState("");
  const [initialData, setInitialData] = useState(null);
  const [practiceLogoFilename, setPracticeLogoFilename] = useState("");
  const [buttonText, setButtonText] = useState("Saved");
  const [status, setStatus] = useState(null);

  const getContactData = async () => {
    try {
      const response = await getContactInfo(providerId);
      const decryptedData = decryptData(
        response?.data?.data?.practiceContactInfo
      );  
      const statusData = decryptData(response?.data?.data?.providerStatus);
      setInitialData(decryptedData);
      setStatus(statusData);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    getContactData();
  }, [providerId]);

  useEffect(() => {
    if (initialData) {
      formik.setValues({
        officeName: initialData?.practiceNameAndAddress?.officeName || "",
        streetAddress: initialData?.practiceNameAndAddress?.streetAddress || "",
        country: initialData?.practiceNameAndAddress?.country || "",
        state: initialData?.practiceNameAndAddress?.state || "",
        city: initialData?.practiceNameAndAddress?.city || "",
        pinCode: initialData?.practiceNameAndAddress?.pinCode || "",
        phone: initialData?.practiceContactInformation?.phone || "",
        fax: initialData?.practiceContactInformation?.fax || "",
        website: initialData?.practiceContactInformation?.website || "",
        email: initialData?.practiceContactInformation?.email || "",
        // bvdqEmail: status?.email || "",
         bvdqEmail: Array.isArray(initialData?.locationDetails?.bvdqEmail) 
        ? initialData.locationDetails.bvdqEmail.filter(email => email && email.trim())
        : initialData?.locationDetails?.bvdqEmail 
          ? [initialData.locationDetails.bvdqEmail]
          : [""],
        managerFirstName:
          initialData?.practiceManagerAdministrator?.firstName || "",
        managerLastName:
          initialData?.practiceManagerAdministrator?.lastName || "",
        managerEmail: initialData?.practiceManagerAdministrator?.email || "",
        managerPhone: initialData?.practiceManagerAdministrator?.phone || "",
        ownerFirstName: initialData?.ownerContactInformation?.firstName || "",
        ownerLastName: initialData?.ownerContactInformation?.lastName || "",
        ownerEmail: initialData?.ownerContactInformation?.email || "",
        ownerPhone: initialData?.ownerContactInformation?.phone || "",
        notes: initialData?.ownerContactInformation?.notes || "",
        practiceLogo: initialData?.practiceLogo || null,
        locationStatus: initialData?.locationDetails?.locationStatus || false,
        aboutMe: initialData?.locationDetails?.aboutMe || "",
        medicalInsuranceStatus:
          initialData?.locationDetails?.medicalInsuranceStatus || false,
        insurance_details:
          initialData?.locationDetails?.insurance_details || [],
      });
      if (initialData?.practiceLogo) {
        const imageUrl = `${path}/${initialData?.practiceLogo
          .split("/")
          .pop()}`;
        // Fetch the image from the provided URL
        fetch(imageUrl).then(async (response) => {
          // Convert the image to a Blob
          const blob = await response.blob();
          // Create a File object from the Blob
          const file = new File([blob], imageUrl.split("/").pop(), {
            type: blob.type,
          });
          // Create a DataTransfer object to simulate a file being selected
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(file);

          setPracticeLogoFilename(initialData?.practiceLogo.split("/").pop());

          setFieldValue("practiceLogo", dataTransfer.files[0]);
        });
      }
      setButtonText("Saved");
    }
  }, [initialData]);

  const formik = useFormik({
    initialValues: {
      managerFirstName: "",
      managerLastName: "",
      managerEmail: "",
      managerPhone: "",
      ownerFirstName: "",
      ownerLastName: "",
      ownerEmail: "",
      ownerPhone: "",
      notes: "",
    },
    validationSchema: stepTwelveSchema,
    onSubmit: async (values) => {
      try {
          const transformedValues = {
        practiceNameAndAddress: {
          officeName: values.officeName,
          streetAddress: values.streetAddress,
          city: values.city,
          state: values.state,
          stateName: values.stateName,
          country: values.country,
          pinCode: values.pinCode,
          lastName: values.lastName
        },
        practiceContactInformation: {
          phone: values.phone,
          fax: values.fax,
          website: values.website,
          email: values.email
        },
        locationDetails: {
          aboutMe: values.aboutMe,
          locationStatus: values.locationStatus,
          medicalInsuranceStatus: values.medicalInsuranceStatus,
          insurance_details: values.insurance_details,
          // Filter out empty emails and send as array
          bvdqEmail: Array.isArray(values.bvdqEmail) 
            ? values.bvdqEmail.filter(email => email && email.trim())
            : [values.bvdqEmail].filter(email => email && email.trim())
        },
        practiceManagerAdministrator: {
          firstName: values.managerFirstName,
          lastName: values.managerLastName,
          email: values.managerEmail,
          phone: values.managerPhone
        },
        ownerContactInformation: {
          firstName: values.ownerFirstName,
          lastName: values.ownerLastName,
          email: values.ownerEmail,
          phone: values.ownerPhone,
          notes: values.notes
        },
        practiceLogo: values.practiceLogo
      };
        // setEnableFields(true);
        const response = await submitContactInfo(providerId, transformedValues);

      let obj = {
        event: `SuperAdmin has updated Practice Contact Information for location ${loc.organization_name}.`,
      };

      await createAuditLogs({ data: encryptData(obj) }, headers);
      toast.success("Information updated successfully");
      setButtonText("Saved");
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error?.response?.data?.message || "An error occurred");
    }
  },
  });

  const { values, errors, touched, handleChange, handleSubmit, setFieldValue } =
    formik;
  // Get options for countries, states, and cities
  const countries = Country.getAllCountries();
  const states = values.country ? State.getStatesOfCountry(values.country) : [];
  const cities = values.state
    ? City.getCitiesOfState(values.country, values.state)
    : [];

  const handleFieldChange = (e) => {
    handleChange(e);
    setButtonText("Save Changes"); // Change button text when a field is modified
  };

  return (
    <div className="panel-box">
      <form className="sectionalForms" onSubmit={handleSubmit}>
        <div className="title"></div>
        <div className="row">
          <div className="subtitle">Owner Contact Information</div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="ownerFirstName"
                label="First Name"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.ownerFirstName}
                onChange={handleFieldChange}
                helperText={touched.ownerFirstName && errors.ownerFirstName}
                error={touched.ownerFirstName && !!errors.ownerFirstName}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="ownerLastName"
                label="Last Name"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.ownerLastName}
                onChange={handleFieldChange}
                helperText={touched.ownerLastName && errors.ownerLastName}
                error={touched.ownerLastName && !!errors.ownerLastName}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="ownerEmail"
                label="Owner Email"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.ownerEmail}
                onChange={handleFieldChange}
                helperText={touched.ownerEmail && errors.ownerEmail}
                error={touched.ownerEmail && !!errors.ownerEmail}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="ownerPhone"
                label="Phone"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.ownerPhone}
                onChange={handleFieldChange}
                helperText={touched.ownerPhone && errors.ownerPhone}
                error={touched.ownerPhone && !!errors.ownerPhone}
                // disabled={enableField}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="subtitle">
            Practice Manager/Administrator (use Owner if none exists)
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="managerFirstName"
                label="First Name"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.managerFirstName}
                onChange={handleFieldChange}
                helperText={touched.managerFirstName && errors.managerFirstName}
                error={touched.managerFirstName && !!errors.managerFirstName}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="managerLastName"
                label="Last Name"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.managerLastName}
                onChange={handleFieldChange}
                helperText={touched.managerLastName && errors.managerLastName}
                error={touched.managerLastName && !!errors.managerLastName}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="managerEmail"
                label="Manager Email"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.managerEmail}
                onChange={handleFieldChange}
                helperText={touched.managerEmail && errors.managerEmail}
                error={touched.managerEmail && !!errors.managerEmail}
                // disabled={enableField}
              />
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12">
            <div className="formdesign">
              <TextField
                id="outlined-basic"
                name="managerPhone"
                label="Phone"
                className="w-100"
                variant="outlined"
                size="small"
                value={values.managerPhone}
                onChange={handleFieldChange}
                helperText={touched.managerPhone && errors.managerPhone}
                error={touched.managerPhone && !!errors.managerPhone}
                // disabled={enableField}
              />
            </div>
          </div>
        </div>

        <div className="row">
          <div className="subtitle">Additional Location Contact Notes</div>
          <div className="col-lg-12 col-md-12 col-sm-12">
            <div className="formdesign">
              <TextField
                label="Additional Notes About Locations's contact"
                variant="outlined"
                multiline
                rows={4}
                value={values.notes}
                helperText={touched.notes && errors.notes}
                error={touched.notes && !!errors.notes}
                onChange={(event) => {
                  setNotes(event.target.value);
                  formik.setFieldValue("notes", event.target.value);
                  setButtonText("Save Changes");
                }}
                fullWidth
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-12 col-md-12 col-sm-12">
            <div className="text-end">
              <div className="text-end modalactions">
                <button className="btn btn-blacked" type="submit">
                  {buttonText}
                </button>
              </div>
              {/* )} */}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
